import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Hireun</title>
        <meta name="description" content="Hireun Privacy Policy - Learn how we collect, use, and protect your personal data." />
        <link rel="canonical" href="https://hireun.com/privacy" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: January 2025</p>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Hireun ("we", "our", or "us") is committed to protecting your privacy. This Privacy 
              Policy explains how we collect, use, disclose, and safeguard your information when 
              you use our interview preparation service.
            </p>
            <p className="text-gray-700 mb-4">
              We comply with the General Data Protection Regulation (GDPR) and other applicable 
              data protection laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Account Information:</strong> Email address, name, and password when you register</li>
              <li><strong>CV/Resume:</strong> Documents you upload for interview preparation</li>
              <li><strong>Job Descriptions:</strong> Job postings you provide for analysis</li>
              <li><strong>Payment Information:</strong> Processed securely by our payment provider (Stripe)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Device and browser information</li>
              <li>IP address and approximate location</li>
              <li>Usage data and interaction with our service</li>
              <li>Cookies and similar technologies</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Provide and improve our interview preparation service</li>
              <li>Generate personalized interview content using AI</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send service-related communications</li>
              <li>Analyze usage to improve our service</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. CV Storage and Security</h2>
            <p className="text-gray-700 mb-4">
              Your CV is stored securely using industry-standard encryption. We:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Encrypt all uploaded documents at rest and in transit</li>
              <li>Store documents in secure cloud infrastructure (AWS)</li>
              <li>Never share your CV with third parties</li>
              <li>Allow you to delete your documents at any time</li>
              <li>Automatically delete documents upon account deletion</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. AI Processing</h2>
            <p className="text-gray-700 mb-4">
              We use artificial intelligence to analyze your CV and job descriptions. This involves:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Processing your documents through our AI systems to generate interview content</li>
              <li>Using third-party AI providers (OpenAI) under strict data processing agreements</li>
              <li>Not using your personal documents to train AI models</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Data Sharing</h2>
            <p className="text-gray-700 mb-4">
              We do not sell your personal data. We may share data with:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Service Providers:</strong> Who help us operate our service (hosting, payment processing)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger or acquisition</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Your Rights (GDPR)</h2>
            <p className="text-gray-700 mb-4">
              Under GDPR, you have the right to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate personal data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your data</li>
              <li><strong>Restriction:</strong> Request restricted processing of your data</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise these rights, contact us at{" "}
              <a href="mailto:dpo@hireun.com" className="text-blue-600 hover:underline">
                dpo@hireun.com
              </a>
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal data for as long as necessary to provide our service and 
              comply with legal obligations. When you delete your account:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Your profile and CV are deleted within 30 days</li>
              <li>Anonymized usage data may be retained for analytics</li>
              <li>Legal and financial records are retained as required by law</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Cookies</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies for:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Essential Cookies:</strong> Required for the service to function</li>
              <li><strong>Analytics Cookies:</strong> To understand how users interact with our service</li>
              <li><strong>Preference Cookies:</strong> To remember your settings</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. International Transfers</h2>
            <p className="text-gray-700 mb-4">
              Your data may be transferred to and processed in countries outside the European 
              Economic Area (EEA). We ensure appropriate safeguards are in place, including 
              Standard Contractual Clauses approved by the European Commission.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your data, 
              including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Encryption in transit (TLS/SSL) and at rest</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Employee training on data protection</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our service is not intended for children under 16. We do not knowingly collect 
              personal data from children.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of 
              significant changes via email or through our service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For questions about this Privacy Policy or your personal data:
            </p>
            <ul className="list-none mb-4 text-gray-700">
              <li><strong>Email:</strong>{" "}
                <a href="mailto:dpo@hireun.com" className="text-blue-600 hover:underline">
                  dpo@hireun.com
                </a>
              </li>
              <li><strong>General Inquiries:</strong>{" "}
                <a href="mailto:contact@hireun.com" className="text-blue-600 hover:underline">
                  contact@hireun.com
                </a>
              </li>
              <li><strong>Location:</strong> Paris, France</li>
            </ul>
          </div>
        </div>

        <PublicFooter />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
